/**
 * The office shift log (WT-2, fieldiq-api#1245), a full page:
 *   /well-testing/jobs/:jobId/shift-logs/new — write one (POST)
 *   /well-testing/shift-logs/:id             — read, edit (PATCH), delete
 *
 * A log is one job, one day, one shift (day or night). The read shows the crew
 * present, the shift's hours and standby, each unit's hours (the hour-meter end
 * on OWNED units only) and the totals the API returns — nothing is summed on
 * this side. Edit opens the same form inline. A save with a `warnings` entry
 * (meter_below_previous) went through and says so in an amber notice.
 *
 * The day defaults to `meta.today` — the tenant's today, from the API. Writing
 * is WT_WORK (a field hand must also be on the crew: 403 not_on_crew reads as a
 * refusal); deleting is WT_DISPATCH. A closed job's log is read-only.
 */
import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, CalendarDays, Clock, Pencil, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import {
  useCreateWtShiftLog,
  useDeleteWtShiftLog,
  useUpdateWtShiftLog,
  useWtJob,
  useWtMeta,
  useWtShiftLog,
} from '../../hooks/useWellTesting';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canDispatchWt, canWorkWt, fmtWtNumber, wtDay, wtJobLoggable, wtRefusal, wtWarningText } from '../../lib/well-testing';
import type { WtShiftLog, WtShiftLogWarning } from '../../types/well-testing';
import { useBackToList } from '../downhole/recordPage';
import { officeLogOptions, officeLogValues } from './logOptions';
import { ShiftLogEditor } from './ShiftLogEditor';
import {
  isNotFound,
  WT_BAND_SECONDARY,
  WT_EYEBROW,
  WT_PRIMARY,
  WT_SECONDARY,
  WtDialog,
  WtEmpty,
  WtFormError,
  WtLoading,
  WtPanel,
  WtRegister,
  WtSpec,
  type WtColumn,
} from './WtBits';

const SETTLED = { isLoading: false };
const DEFAULT_SHIFTS = [
  { value: 'day', label: 'Day' },
  { value: 'night', label: 'Night' },
];

export function LogWarnings({ warnings, unitName }: { warnings: WtShiftLogWarning[]; unitName: (equipmentId?: string) => string | null }) {
  if (!warnings.length) return null;
  return (
    <div role="status" data-testid="wt-log-warnings" className="flex gap-3 rounded-fi-control border border-fi-amber/60 bg-fi-amber/10 px-4 py-3 text-sm text-fieldiq-text">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fi-amber" aria-hidden="true" />
      <ul className="space-y-1">
        {warnings.map((w, i) => (
          <li key={i}>{wtWarningText(w, unitName(w.equipmentId ?? w.equipment_id))}</li>
        ))}
      </ul>
    </div>
  );
}

type UnitLine = WtShiftLog['units'][number];

function LogReadout({ log }: { log: WtShiftLog }) {
  const columns: WtColumn<UnitLine>[] = [
    {
      key: 'unit',
      header: 'Unit',
      render: (u) => (
        <>
          <p className="font-extrabold text-white">{u.unitName || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">
            {u.kind === 'rent_in' ? `Rent-in${u.vendorName ? ` · ${u.vendorName}` : ''}` : 'Owned'}
            {u.unitNumber ? ` · ${u.unitNumber}` : ''}
          </p>
        </>
      ),
    },
    { key: 'hours', header: 'Hours', align: 'right', render: (u) => fmtWtNumber(u.hours) },
    { key: 'standby', header: 'Standby', align: 'right', render: (u) => fmtWtNumber(u.standbyHours) },
    // A rent-in is not metered here; `—` there is "not recorded", never 0.
    { key: 'meter', header: 'Hour meter end', align: 'right', render: (u) => (u.kind === 'owned' ? fmtWtNumber(u.hourMeterEnd) : '—') },
    { key: 'notes', header: 'Notes', render: (u) => u.notes || '—' },
  ];
  return (
    <>
      <WtPanel eyebrow={log.shiftLabel ? `${log.shiftLabel} shift` : 'Shift'} title={wtDay(log.workDate)} testId="wt-log-readout">
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <WtSpec label="Shift hours" value={fmtWtNumber(log.hours)} mono />
          <WtSpec label="Standby hours" value={fmtWtNumber(log.standbyHours)} mono />
          <WtSpec label="Owned unit hours" value={fmtWtNumber(log.totals?.ownedEquipmentHours)} mono />
          <WtSpec label="Rent-in hours" value={fmtWtNumber(log.totals?.rentInHours)} mono />
          <WtSpec label="Crew present" value={log.crewPresent.length ? log.crewPresent.map((c) => c.userName || '—').join(', ') : '—'} />
          <WtSpec label="Written by" value={log.createdByName || '—'} />
          <WtSpec label="Last saved by" value={log.updatedByName || '—'} />
        </dl>
        {log.safetyNotes && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text"><span className="font-bold">Safety: </span>{log.safetyNotes}</p>}
        {log.handoverNote && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text"><span className="font-bold">Handover: </span>{log.handoverNote}</p>}
        {log.notes && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{log.notes}</p>}
      </WtPanel>
      <WtPanel eyebrow="Units" title="Unit hours" testId="wt-log-units-readout">
        <WtRegister
          query={SETTLED}
          rows={log.units}
          columns={columns}
          rail={() => 'border-l-transparent'}
          subject="the unit hours"
          testId="wt-log-unit"
          minWidth="min-w-[640px]"
          empty={<WtEmpty>No unit hours on this log.</WtEmpty>}
          card={(u) => (
            <p className="text-sm text-fieldiq-text">
              <span className="font-extrabold text-white">{u.unitName || '—'}</span> · {fmtWtNumber(u.hours)} h
              {u.kind === 'owned' && u.hourMeterEnd != null ? ` · meter ${fmtWtNumber(u.hourMeterEnd)}` : ''}
            </p>
          )}
        />
      </WtPanel>
    </>
  );
}

export default function WtShiftLogScreen() {
  const { id, jobId: jobParam } = useParams<{ id?: string; jobId?: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const logQuery = useWtShiftLog(id);
  const log = logQuery.data?.data ?? null;
  const jobId = jobParam ?? log?.jobId;
  const jobQuery = useWtJob(jobId);
  const job = jobQuery.data?.data ?? null;
  const metaQuery = useWtMeta();
  const today = jobQuery.data?.meta.today ?? metaQuery.data?.meta.today ?? '';
  const shifts = metaQuery.data?.data?.shifts ?? DEFAULT_SHIFTS;
  const back = useBackToList(jobId ? `/well-testing/jobs/${jobId}?tab=logs` : '/well-testing/jobs');
  const create = useCreateWtShiftLog();
  const update = useUpdateWtShiftLog();
  const del = useDeleteWtShiftLog();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<WtShiftLogWarning[]>((location.state as { warnings?: WtShiftLogWarning[] } | null)?.warnings ?? []);
  const [day, setDay] = useState<{ workDate: string; shift: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);

  const loggable = !!job && wtJobLoggable(job.status);
  const canWrite = canWorkWt(user?.role) && loggable;
  const canDelete = canDispatchWt(user?.role) && !!log && loggable;
  const options = job ? officeLogOptions(job) : { crew: [], units: [] };
  const unitName = (equipmentId?: string) => options.units.find((u) => u.kind === 'owned' && u.equipmentId === equipmentId)?.name ?? null;
  const current = day ?? { workDate: log?.workDate ?? today, shift: log?.shift ?? 'day' };

  const primary = isNew ? jobQuery : logQuery;
  const failure = queryLoadFailure(primary) ?? (isNew ? null : queryLoadFailure(jobQuery));
  const loading = primary.isLoading || (!isNew && !!log && jobQuery.isLoading) || (isNew && !today && metaQuery.isLoading);
  const notFound = isNotFound(queryFailureError(primary));

  const title = isNew ? 'New shift log' : log ? `${wtDay(log.workDate)} · ${log.shiftLabel ?? log.shift}` : logQuery.isLoading ? 'Shift log' : '—';

  return (
    <div data-testid="wt-shift-log-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={job?.jobNumber ? `${WT_EYEBROW} · ${job.jobNumber}` : WT_EYEBROW}
        title={title}
        description={job ? `${job.customerName ?? '—'} · ${job.wellName ?? '—'}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          !isNew && log && (canWrite || canDelete) ? (
            <div className="flex flex-wrap gap-2">
              {canWrite && (
                <button type="button" className={WT_BAND_SECONDARY} disabled={editing} onClick={() => { setError(null); setDay(null); setEditing(true); }}>
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit
                </button>
              )}
              {canDelete && (
                <button type="button" className={WT_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={
          log
            ? [
                { icon: <CalendarDays className="size-4" aria-hidden="true" />, label: 'Shift', tone: 'neutral', value: log.shiftLabel ?? log.shift },
                { icon: <Clock className="size-4" aria-hidden="true" />, label: 'Hours', tone: 'neutral', value: fmtWtNumber(log.hours) },
                { icon: <Users className="size-4" aria-hidden="true" />, label: 'Crew', tone: 'neutral', value: String(log.crewPresent.length) },
              ]
            : undefined
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {loading ? (
            <WtLoading label="Loading shift log" />
          ) : notFound || (!failure && (isNew ? !job : !log)) ? (
            <WtPanel eyebrow="Shift log" title="Shift log">
              <WtEmpty>{isNew ? 'This job was not found. It may have been deleted.' : 'This shift log was not found. It may have been deleted.'}</WtEmpty>
            </WtPanel>
          ) : failure ? (
            <WtPanel eyebrow="Shift log" title="Shift log">
              <LoadFailureNotice error={queryFailureError(queryLoadFailure(primary) ? primary : jobQuery)} kind={failure} subject={isNew ? 'this job' : 'this shift log'} />
            </WtPanel>
          ) : (
            <>
              <LogWarnings warnings={warnings} unitName={unitName} />
              {job && !loggable && (
                <p className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text" data-testid="wt-log-locked">
                  {job.status === 'draft'
                    ? 'This job is still a draft. Schedule it before logging a shift on it.'
                    : 'This job is completed or cancelled, so its logs can no longer be changed.'}
                </p>
              )}
              {!isNew && log && <LogReadout log={log} />}
              {(isNew ? canWrite : editing && canWrite) && job && (
                <WtPanel eyebrow={isNew ? 'New' : 'Edit'} title={isNew ? 'Log a shift' : 'Edit shift log'} testId="wt-log-edit">
                  <div className="px-4 py-4">
                    <ShiftLogEditor
                      key={`${log?.id ?? 'new'}-${formKey}`}
                      workDate={current.workDate}
                      shift={current.shift}
                      shifts={shifts}
                      onDayShift={setDay}
                      values={officeLogValues(log)}
                      crewOptions={options.crew}
                      unitOptions={options.units}
                      pending={create.isPending || update.isPending}
                      error={error}
                      submitLabel={isNew ? 'Save shift log' : 'Save changes'}
                      onCancel={isNew ? back : () => setEditing(false)}
                      onSubmit={(body) => {
                        setError(null);
                        if (current.workDate && today && current.workDate > today)
                          return setError(`A shift log cannot be for a future day. Today is ${wtDay(today)}.`);
                        const full = { ...body, work_date: current.workDate || undefined, shift: current.shift };
                        if (isNew) {
                          create.mutate(
                            { jobId: job.id, body: full },
                            {
                              onSuccess: (res) => {
                                toast.success('Shift log saved');
                                if (res.data?.id) navigate(`/well-testing/shift-logs/${res.data.id}`, { replace: true, state: { warnings: res.warnings } });
                              },
                              onError: (err) => setError(wtRefusal(err, 'The shift log could not be saved.')),
                            },
                          );
                        } else if (log) {
                          update.mutate(
                            { id: log.id, body: full },
                            {
                              onSuccess: (res) => {
                                toast.success('Shift log saved');
                                setWarnings(res.warnings);
                                setEditing(false);
                                setDay(null);
                                setFormKey((k) => k + 1);
                              },
                              onError: (err) => setError(wtRefusal(err, 'The shift log could not be saved.')),
                            },
                          );
                        }
                      }}
                    />
                  </div>
                </WtPanel>
              )}
              {isNew && job && loggable && !canWrite && (
                <WtPanel eyebrow="Shift log" title="Shift log">
                  <WtEmpty>Only the office, a supervisor, a dispatcher or a field hand on the crew can log a shift.</WtEmpty>
                </WtPanel>
              )}
            </>
          )}
        </div>
      </section>

      {log && canDelete && (
        <WtDialog
          open={deleting}
          title="Delete shift log"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={WT_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                Keep log
              </button>
              <button
                type="button"
                className={WT_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(log.id, {
                    onSuccess: () => {
                      toast.success('Shift log deleted');
                      navigate(`/well-testing/jobs/${log.jobId}?tab=logs`, { replace: true });
                    },
                    onError: (err) => setDeleteError(wtRefusal(err, 'The shift log could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete log'}
              </button>
            </>
          }
        >
          <WtFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">
            Removes the {log.shiftLabel?.toLowerCase() ?? log.shift} shift log for {wtDay(log.workDate)}. Its hour-meter readings are removed with it.
          </p>
        </WtDialog>
      )}
    </div>
  );
}
